export const IMAGE_DOC_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'] as const

/** Matches scripts/services/image-document.js upload caps. */
export const IMAGE_IMPORT_MAX_RASTER_BYTES = 10 * 1024 * 1024
export const IMAGE_IMPORT_MAX_SVG_BYTES = 500 * 1024

export type ImageDocumentStub = {
  kind?: string
  mime_type?: string
  storage_path?: string
  inline_svg?: string
  width?: number | null
  height?: number | null
  byte_size?: number | null
}

export function hasImageDocExtension(filePath?: string | null): boolean {
  const lower = String(filePath || '').toLowerCase()
  return IMAGE_DOC_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

export function imageImportSizeLimit(filename: string): number {
  return String(filename || '').toLowerCase().endsWith('.svg')
    ? IMAGE_IMPORT_MAX_SVG_BYTES
    : IMAGE_IMPORT_MAX_RASTER_BYTES
}

/** Folder drag-and-drop: PNG/JPG/GIF/WebP/SVG, with the same size caps as the API. */
export function checkDroppedImageImport(file: {
  name: string
  size?: number
}): { ok: true } | { ok: false; reason: string } {
  if (!hasImageDocExtension(file.name)) {
    return { ok: false, reason: `${file.name}: not an image` }
  }
  const max = imageImportSizeLimit(file.name)
  if (typeof file.size === 'number' && file.size > max) {
    const label = file.name.toLowerCase().endsWith('.svg') ? '500 KB' : '10 MB'
    return { ok: false, reason: `${file.name} is too large (max ${label})` }
  }
  return { ok: true }
}

export function parseImageDocumentStub(content?: string | null): ImageDocumentStub | null {
  if (!content || typeof content !== 'string') return null
  const trimmed = content.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed) as ImageDocumentStub
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.kind === 'image' || parsed.storage_path || parsed.inline_svg) return parsed
    return null
  } catch {
    return null
  }
}

export function imageDocumentSrc(documentId: string): string {
  return `/api/images/${documentId}/image`
}

export function formatByteSize(bytes?: number | null): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
